import { useEffect, useState } from "react";
import axios from "axios";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { opDeMac, openOpDeMac } from "@/lib/op-de-mac";

/**
 * CA6 (besluit Frank 1 november 2026): een repo meenemen.
 *
 * Als bundel (alle takken met geschiedenis; `git clone <bestand>` maakt er weer een repo van) of als zip van de gekozen
 * tak (alleen de bestanden). De server geeft de inhoud in base64: de tunnel naar de telefoon draagt tekst, en de
 * Mac-app zet base64 via de schil in Downloads (`openOpDeMac`). Op de telefoon wordt het een download van de browser.
 * Daaronder hoe je van buiten NovaMund kloont. Een kopie buiten deze Mac komt pas met EV2.
 */
export function bytesUit(base64) {
  const ruw = atob(base64 || "");
  const bytes = new Uint8Array(ruw.length);
  for (let i = 0; i < ruw.length; i += 1) bytes[i] = ruw.charCodeAt(i);
  return bytes;
}

export default function RepoMeenemen({ repo, tak }) {
  const { t } = useTaal();
  const [bezig, setBezig] = useState("");
  const [kloon, setKloon] = useState(null);

  useEffect(() => {
    axios.get(`${API}/repos/${repo.id}/meenemen`).then((r) => setKloon(r.data)).catch(() => setKloon(null));
  }, [repo.id]);

  async function haal(soort) {
    setBezig(soort);
    try {
      const { data } = await axios.get(`${API}/repos/${repo.id}/export`, { params: { soort, tak: soort === "zip" ? tak : "" } });
      if (opDeMac()) {
        const uit = await openOpDeMac({ name: data.naam, data: data.data });
        if (uit?.fout) throw new Error(uit.fout);
        toast.success(t("{naam} staat in Downloads.", { naam: data.naam }));
      } else {
        const adres = URL.createObjectURL(new Blob([bytesUit(data.data)], { type: "application/octet-stream" }));
        const a = document.createElement("a");
        a.href = adres;
        a.download = data.naam;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(adres), 10000);
        toast.success(t("{naam} wordt gedownload.", { naam: data.naam }));
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? t(detail) : t("Meenemen lukte niet."));
    } finally {
      setBezig("");
    }
  }

  return (
    <div className="space-y-3" data-testid="repo-meenemen">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="tap-target" disabled={Boolean(bezig)} onClick={() => haal("bundel")}
                data-testid="meenemen-bundel">
          {bezig === "bundel" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
          {t("Download als bundel, met geschiedenis")}
        </Button>
        <Button size="sm" variant="outline" className="tap-target" disabled={Boolean(bezig)} onClick={() => haal("zip")}
                data-testid="meenemen-zip">
          {bezig === "zip" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
          {t("Download {tak} als zip", { tak })}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {t("Van een bundel maak je overal weer een repo met git clone <bestand>, of hier met Importeren. Hooguit 10 MB.")}
      </p>
      {kloon && (
        <div className="space-y-1" data-testid="meenemen-kloon">
          <p className="text-xs text-muted-foreground">{t("Klonen op deze Mac, buiten NovaMund, in Terminal:")}</p>
          <code className="block break-all rounded-md border bg-muted/30 p-2 text-xs">{kloon.kloonopdracht}</code>
        </div>
      )}
      <p className="text-xs text-muted-foreground">{t("Een kopie buiten deze Mac komt later, met je keuze bij EV2.")}</p>
    </div>
  );
}
